/**
 * REST-клиент SPECTER Core.
 *
 * Единственный источник данных для терминала. Ни одного обращения к бирже
 * здесь нет и появиться не может: терминал не знает ни её адреса, ни её
 * форматов — он знает только Core.
 */

import type {
  BotConstraints,
  FiboView,
  BotInstrumentRef,
  DerivativePoint,
  BotManifest,
  BotStats,
  BotStatus,
  InstrumentStats,
  MarketBrief,
  MarketIntelligence,
  MarketMood,
  NewsDigest,
  OrderBookSnapshot,
  OrderFlowFeatures,
  OrderFlowSnapshot,
  Position,
  RadarEvent,
  RadarSnapshot,
  RadarTypeStats,
  Side,
  Signal,
  SymbolInfo,
  Ticker,
  SignalSpec,
  StrategyBot,
  StrategyConfig,
  Timeframe,
  Trade,
  TradingMode,
  UserSettings,
  BotDefinition,
  WorldAuthor,
  WorldEvent,
  WorldRatings,
  WorldSystem,
  WorldSystemPage,
  WorldTrader,
  SeasonView,
  WorldIndicator,
  WorldIndicatorPage,
} from '@specter/types';
import type { Fill as StrategyFill, SniperPosition, SniperSizing, SniperVenue, SpecChange, SpecFunnel, SpecReport, StrategyStats, Suggestion } from '@specter/rules';

/** В dev-режиме пусто: запросы идут через прокси Vite на Core. */
const BASE = (import.meta.env.VITE_CORE_URL as string | undefined) ?? '';

export interface VenueInfo {
  readonly id: string;
  readonly symbols: number;
  readonly capabilities: {
    readonly timeframes: readonly Timeframe[];
    readonly maxHistoryBars: number;
    readonly streams: readonly string[];
  };
}

/** Свеча в транспортном виде: [time, o, h, l, c, v]. */
export type CandleTuple = [number, number, number, number, number, number];

export interface GapRange {
  readonly from: number;
  readonly to: number;
}

export interface CandlesResponse {
  readonly venue: string;
  readonly symbol: string;
  readonly tf: Timeframe;
  readonly candles: CandleTuple[];
  readonly gaps: GapRange[];
}

export interface DerivativeResponse {
  readonly venue: string;
  readonly symbol: string;
  readonly tf: Timeframe;
  readonly points: DerivativePoint[];
}

const DERIVATIVE_PATHS = {
  oi: '/api/open-interest',
  funding: '/api/funding',
  premium: '/api/premium',
  spotDelta: '/api/spot-delta',
  futuresDelta: '/api/futures-delta',
  futuresBuyShare: '/api/futures-buy-share',
  longAccounts: '/api/positioning/accounts',
  topPositions: '/api/positioning/top',
  miScore: '/api/intelligence/score',
  liqLong: '/api/liquidations/long',
  liqShort: '/api/liquidations/short',
  liqCount: '/api/liquidations/count',
} as const;

import { authToken, forgetSession } from '../state/session.js';

export class CoreApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'CoreApiError';
  }
}

export interface BotInfo {
  readonly connectionId: string;
  readonly manifest: BotManifest;
  readonly status: BotStatus;
  readonly instruments: readonly BotInstrumentRef[];
  /** Границы выбора: что разрешил автор бота и что может дать платформа. */
  readonly constraints: BotConstraints;
  /** Потоки, отвалившиеся при последнем подключении, с причиной. */
  readonly failed: ReadonlyArray<{
    instrument: BotInstrumentRef;
    reason: string;
  }>;
  readonly params: Readonly<Record<string, unknown>>;
  readonly diagnostics: {
    readonly latencyMs: number | null;
    readonly missedPongs: number;
    readonly signalsReceived: number;
    readonly reason: string | undefined;
  };
}

/**
 * Заголовки запроса вместе с токеном сеанса.
 *
 * Токен идёт заголовком, а не параметром адреса: адреса оседают в
 * журналах прокси и серверов, и токен утёк бы туда вместе с каждым
 * запросом. По той же причине заголовком ходят и ключи, которые Core
 * отдаёт наружу, — ни один из них не появляется в адресе.
 */
function headers(extra: Record<string, string> = {}): Record<string, string> {
  const token = authToken();
  return {
    accept: 'application/json',
    ...extra,
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Core сказал «не пущу».
 *
 * Токен мог истечь или быть закрыт с другого устройства. Держаться за
 * него дальше незачем: сеанс забывается, и оболочка сама показывает
 * экран входа — она подписана на сеанс.
 *
 * Маршруты самого входа исключены: их `401` — это «неверный пароль», и
 * забывать по нему нечего.
 */
function checkAuth(status: number, path: string): void {
  if (status === 401 && !path.startsWith('/api/auth/')) forgetSession();
}

async function send<T>(method: 'PUT' | 'POST' | 'DELETE', path: string, body: unknown): Promise<T> {
  const url = new URL(`${BASE}${path}`, globalThis.location.origin);
  const response = await fetch(url.toString(), {
    method,
    headers: headers({ 'content-type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    checkAuth(response.status, path);
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new CoreApiError(
      response.status,
      payload?.error ?? `Core ответил ${response.status} на ${path}`,
    );
  }
  return (await response.json()) as T;
}

async function get<T>(path: string, params: Record<string, string | number> = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, globalThis.location.origin);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, String(value));
  }

  const response = await fetch(url.toString(), { headers: headers() });
  if (!response.ok) {
    checkAuth(response.status, path);
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new CoreApiError(
      response.status,
      body?.error ?? `Core ответил ${response.status} на ${path}`,
    );
  }
  return (await response.json()) as T;
}

/** Виртуальный счёт человека для ботов-стратегий. */
export interface StrategyAccount {
  readonly balance: number;
  readonly startBalance: number;
  readonly inBots: number;
  /** Из них — в монетах, купленных ботами по сигналу. */
  readonly inSignals?: number;
  readonly total: number;
  readonly resets: number;
}

/** Бот-стратегия так, как его видит терминал: настройки, итоги, кривая, сделки, линии. */
export interface StrategyView {
  readonly bot: StrategyBot;
  readonly stats: StrategyStats;
  readonly price: number;
  readonly equity: ReadonlyArray<{ readonly time: number; readonly value: number }>;
  readonly fills: readonly StrategyFill[];
  readonly levels: ReadonlyArray<{ readonly price: number; readonly kind: 'buy' | 'sell' | 'safety' | 'take' }>;
}

export interface StrategiesResponse {
  readonly account: StrategyAccount;
  readonly bots: readonly StrategyView[];
}

/* ---- «Снайпер структуры» ---- */

export interface SniperTradeRecord {
  readonly id: string;
  readonly asset: string;
  readonly long: boolean;
  readonly entry: number;
  readonly sl: number;
  readonly fillPrice: number;
  readonly exitPrice: number;
  readonly signalTime: number;
  readonly fillTime: number;
  readonly exitTime: number;
  readonly reason: 'TP' | 'SL' | 'BE' | 'EXIT' | 'MANUAL';
  readonly pnl: number;
  readonly r: number;
  readonly stopLoss: number;
  readonly hits: readonly boolean[];
  /** Бот на счёте площадки: как сделка прошла там — её итог и цены. `entry: null` — позиции на площадке не было. */
  readonly onVenue?: { readonly pnl: number; readonly entry: number | null; readonly exit: number | null; readonly at: number | null };
  /** Позицию закрыли на площадке не расчётом бота. */
  readonly outside?: boolean;
}

/** Рынок «Снайпера»: тикер биржи и рабочий таймфрейм. */
export interface SniperMarketRef {
  readonly native: string;
  readonly minutes: number;
}

export interface SniperBot {
  readonly id: string;
  readonly name: string;
  readonly venue: SniperVenue;
  /** Счёт площадки, на котором бот повторяет свои сделки; пусто — виртуальный счёт. */
  readonly account?: string;
  readonly markets: readonly SniperMarketRef[];
  readonly sizing: SniperSizing;
  readonly deposit: number;
  readonly cash: number;
  readonly status: 'running' | 'paused' | 'stopped';
  readonly createdAt: number;
  readonly stoppedAt?: number;
  readonly positions: Readonly<Record<string, SniperPosition>>;
  readonly trades: readonly SniperTradeRecord[];
  readonly skips: ReadonlyArray<{ readonly asset: string; readonly time: number; readonly reason: string }>;
  readonly equity: ReadonlyArray<{ readonly time: number; readonly value: number }>;
}

/** Итог бота за всё время: сделок, закрытых, в плюс, сумма и её ход (время, сумма к этому времени). */
export interface BotTotal {
  readonly trades: number;
  readonly closed: number;
  readonly wins: number;
  readonly realized: number;
  readonly curve: ReadonlyArray<readonly [number, number]>;
}

/** Биржа, чей счёт подключается ключом. */
export type ExchangeAccountVenue = 'bybit' | 'bingx' | 'upscale';

/** Счёт внутри ключа Upscale: ключ один, счетов несколько — человек выбирает, какой подключить. */
export interface UpscaleAccountChoice {
  readonly accountId: string;
  readonly type: string;
  readonly phase: string;
  readonly status: string;
  readonly apiTrading: boolean;
  readonly equity: number;
  /** Тип и фаза по-русски: «демо», «испытание, этап 1», «финансируемый». */
  readonly kind: string;
}

/** Подключённый счёт биржи — без ключей: сервер их не отдаёт. */
export interface ExchangeAccount {
  readonly id: string;
  readonly venue: ExchangeAccountVenue;
  readonly mode: 'live' | 'demo';
  readonly label: string;
  /** Последние знаки ключа. */
  readonly tail: string;
  /** Что это за счёт словами площадки — у Upscale: тип и фаза. */
  readonly kind?: string;
  /** Что разрешено ключу; `null` — биржа не раскрыла. */
  readonly rights: { readonly trade: boolean | null; readonly withdraw: boolean | null; readonly ipBound: boolean | null; readonly expiresAt: number | null };
  readonly createdAt: number;
  readonly checkedAt: number;
}

export interface ExchangeAccountOverview {
  readonly money: { readonly currency: string; readonly equity: number; readonly free: number };
  readonly positions: ReadonlyArray<{
    readonly native: string;
    readonly symbol: string;
    readonly side: 'LONG' | 'SHORT';
    readonly quantity: number;
    readonly entryPrice: number;
    readonly unrealized: number;
    readonly leverage: number | null;
    readonly liquidationPrice: number | null;
  }>;
  /** Правила проп-счёта: уровни средств, на которых площадка его закрывает; у биржи — пусто. */
  readonly limits: {
    readonly blocked: string | null;
    readonly equity: number;
    readonly floors: ReadonlyArray<{ readonly name: string; readonly equity: number }>;
  } | null;
  readonly at: number;
}

/**
 * Табло счёта площадки: деньги, кривая средств, позиции с защитой и
 * сделки на этом счёте — одним ответом.
 */
export interface AccountBoard extends Omit<ExchangeAccountOverview, 'positions'> {
  readonly positions: ReadonlyArray<
    ExchangeAccountOverview['positions'][number] & {
      /** Бот, который ведёт позицию; `null` — открыта вручную. */
      readonly botId: string | null;
      /** Стоп на площадке; `null` — стопа нет, поля нет — площадка не ответила. */
      readonly stop?: number | null;
      /** Цель на площадке; `null` — цели нет. */
      readonly take?: number | null;
      /** Цены всех целей позиции, что ещё стоят. */
      readonly targets?: readonly number[];
      /** Уже закрыто по целям у позиции, которая ещё в рынке. */
      readonly fixed?: number;
    }
  >;
  readonly equity: ReadonlyArray<{ readonly time: number; readonly value: number }>;
  readonly trades: readonly Trade[];
  readonly stopped: boolean;
}

/** Отложенный вход: ждёт своей цены на площадке или у нас (виртуальный счёт). */
export interface ManualPending {
  readonly id: string;
  readonly account: string | null;
  readonly venue: string;
  readonly symbol: string;
  readonly side: 'LONG' | 'SHORT';
  readonly kind: 'limit' | 'stop';
  readonly price: number;
  readonly quantity: number;
  readonly stop: number | null;
  readonly take: number | null;
  readonly createdAt: number;
}

/** Ручная позиция виртуального счёта: стоп и цель держит сервер. */
export interface ManualPosition extends Position {
  readonly stop: number | null;
  readonly take: number | null;
}

/** Что с ботом на биржевом счёте: позиция, которую он ведёт, и последняя неудача. */
export interface LiveBotStatus {
  readonly accountId: string;
  /** Позиции, которые бот ведёт на площадке: у Ловца — по одной на монету. */
  readonly positions: ReadonlyArray<{
    readonly symbol: string;
    readonly side: 'LONG' | 'SHORT';
    readonly quantity: number;
    readonly entry: number;
    readonly stop: number;
    readonly targets: ReadonlyArray<{ readonly price: number; readonly quantity: number }>;
  }>;
  readonly problem: { readonly text: string; readonly at: number } | null;
  /** Счёт остановлен человеком: новых входов нет, пока он не возобновит. */
  readonly stopped: boolean;
}

/** Отчёт проверки заявок на демо-счёте: шаг за шагом. */
export interface AccountSelfTest {
  readonly ok: boolean;
  readonly symbol: string;
  readonly steps: ReadonlyArray<{ readonly name: string; readonly ok: boolean; readonly detail: string }>;
}

/** «Отлив» — авторский бот: шорт пампа, который выдохся. */
export interface EbbPosition {
  readonly symbol: string;
  readonly long: false;
  readonly openedAt: number;
  readonly entry: number;
  /** Вершина пампа — в ценах радара. */
  readonly peak: number;
  readonly quantity: number;
  readonly notional: number;
  readonly margin: number;
  readonly leverage: number;
  readonly atStop: number;
}

export interface EbbBot {
  readonly id: string;
  readonly name: string;
  readonly account?: string;
  readonly sizing: SniperSizing;
  readonly maxPositions: number;
  readonly deposit?: number;
  readonly cash?: number;
  readonly status: 'running' | 'paused' | 'stopped';
  readonly createdAt: number;
  readonly stoppedAt?: number;
  readonly positions: Readonly<Record<string, EbbPosition>>;
  readonly closed: ReadonlyArray<{ readonly symbol: string; readonly long: false; readonly openedAt: number; readonly closedAt: number; readonly entry: number; readonly exit: number | null; readonly notional: number; readonly realized: number | null; readonly reason: 'стоп' | 'время' | 'площадка' | 'остановка' }>;
  readonly log: ReadonlyArray<{ readonly time: number; readonly symbol: string; readonly kind: 'watch' | 'enter' | 'skip' | 'exit'; readonly text: string }>;
}

export interface EbbBotView {
  readonly bot: EbbBot;
  readonly realized: number;
  readonly wins: number;
  readonly problem: { readonly text: string; readonly at: number } | null;
  readonly accountStopped: boolean;
  /** Цена сейчас и стоп по монетам с открытой сделкой — в ценах сделки. */
  readonly prices: Readonly<Record<string, number | null>>;
  readonly stops: Readonly<Record<string, number>>;
  /** Пампы, которые бот сейчас караулит. */
  readonly watching: ReadonlyArray<{ readonly symbol: string; readonly peak: number; readonly price: number | null; readonly until: number }>;
  readonly equity: number | null;
  readonly free: number | null;
}

/** «Пружина» — авторский бот: отскок после обвала рынка. */
export interface SpringPosition {
  readonly symbol: string;
  readonly long: true;
  readonly openedAt: number;
  readonly entry: number;
  /** Дно дампа — в ценах радара. */
  readonly low: number;
  readonly quantity: number;
  readonly notional: number;
  readonly margin: number;
  readonly leverage: number;
  readonly atStop: number;
}

export interface SpringBot {
  readonly id: string;
  readonly name: string;
  readonly account?: string;
  readonly sizing: SniperSizing;
  readonly maxPositions: number;
  readonly deposit?: number;
  readonly cash?: number;
  readonly status: 'running' | 'paused' | 'stopped';
  readonly createdAt: number;
  readonly stoppedAt?: number;
  readonly positions: Readonly<Record<string, SpringPosition>>;
  readonly closed: ReadonlyArray<{ readonly symbol: string; readonly long: true; readonly openedAt: number; readonly closedAt: number; readonly entry: number; readonly exit: number | null; readonly notional: number; readonly realized: number | null; readonly reason: 'стоп' | 'время' | 'площадка' | 'остановка' }>;
  readonly log: ReadonlyArray<{ readonly time: number; readonly symbol: string; readonly kind: 'watch' | 'enter' | 'skip' | 'exit'; readonly text: string }>;
}

export interface SpringBotView {
  readonly bot: SpringBot;
  readonly realized: number;
  readonly wins: number;
  readonly problem: { readonly text: string; readonly at: number } | null;
  readonly accountStopped: boolean;
  /** Цена сейчас и стоп по монетам с открытой сделкой — в ценах сделки. */
  readonly prices: Readonly<Record<string, number | null>>;
  readonly stops: Readonly<Record<string, number>>;
  /** Дампы, которые бот сейчас караулит. */
  readonly watching: ReadonlyArray<{ readonly symbol: string; readonly low: number; readonly price: number | null; readonly until: number }>;
  readonly equity: number | null;
  readonly free: number | null;
}

/** «Качели» — авторский бот: корзина «сильные против слабых». */
export interface SeesawBot {
  readonly id: string;
  readonly name: string;
  readonly account?: string;
  readonly capital: number;
  readonly leverage: number;
  /** Аварийный стоп монеты — доля от цены входа (0,3 — 30 %); нет — без стопа. */
  readonly stop?: number;
  readonly deposit?: number;
  readonly cash?: number;
  readonly status: 'running' | 'paused' | 'stopped';
  readonly createdAt: number;
  readonly stoppedAt?: number;
  readonly tranches: ReadonlyArray<{ readonly day: number; readonly longs: readonly string[]; readonly shorts: readonly string[] } | null>;
  readonly lastDay: number | null;
  readonly closed: ReadonlyArray<{ readonly symbol: string; readonly long: boolean; readonly at: number; readonly quantity: number; readonly entry: number; readonly exit: number; readonly realized: number; readonly reason: 'пересборка' | 'стоп' | 'остановка' }>;
  readonly log: ReadonlyArray<{ readonly time: number; readonly kind: 'rebalance' | 'skip' | 'stop' | 'info'; readonly text: string }>;
}

export interface SeesawHoldingView {
  readonly symbol: string;
  readonly long: boolean;
  readonly quantity: number;
  readonly entry: number;
  readonly price: number | null;
  readonly notional: number;
  readonly pnl: number | null;
  /** Ход монеты за месяц — почему она в корзине. */
  readonly change: number | null;
}

export interface SeesawBotView {
  readonly bot: SeesawBot;
  readonly realized: number;
  readonly equity: number | null;
  readonly holdings: readonly SeesawHoldingView[];
  readonly nextAt: number;
  readonly filled: number;
  readonly problem: { readonly text: string; readonly at: number } | null;
  readonly accountStopped: boolean;
}

/** Общий потолок риска счёта площадки: сколько % средств может стоять на стопах всех ботов сразу. */
export interface RiskCapView {
  readonly accountId: string;
  readonly settings: { readonly enabled: boolean; readonly percent: number };
  /** Что на счёте и сколько стоит на стопах; есть, только когда потолок включён. */
  readonly status: { readonly equity: number; readonly atRisk: number; readonly unprotected: number } | null;
}

/** «Режим испытания» — сторож проп-счёта: настройки, что он видит и почему остановил. */
export interface GuardView {
  readonly accountId: string;
  readonly settings: { readonly enabled: boolean; readonly targetPercent: number; readonly reservePercent: number };
  readonly status: {
    readonly equity: number;
    readonly start: number;
    readonly dayStart: number;
    readonly dayResult: number;
    readonly dayLimit: number;
    readonly dayStop: number;
    readonly totalFloor: number;
    readonly totalStop: number;
    readonly profit: number;
    readonly target: number;
    readonly dayCap: number;
    readonly bestDay: number;
    readonly goodDays: number;
    readonly goodDaysNeeded: number;
    readonly goodDayFrom: number;
  } | null;
  readonly lock: { readonly kind: 'day-loss' | 'day-profit' | 'total' | 'target'; readonly text: string; readonly at: number } | null;
  readonly note: string | null;
}

/** «Рикошет» — авторский бот на счёте площадки. Чисел его правила здесь нет намеренно. */
export interface RicochetPosition {
  readonly symbol: string;
  readonly long: boolean;
  readonly openedAt: number;
  readonly entry: number;
  readonly stop: number;
  readonly target: number;
  readonly quantity: number;
  readonly notional: number;
  readonly atStop: number;
  /** Залог и плечо сделки; у сделок, открытых до этих полей, их нет. */
  readonly margin?: number;
  readonly leverage?: number;
  readonly level: number;
}

export interface RicochetBot {
  readonly id: string;
  readonly name: string;
  /** Счёт площадки; пусто — виртуальный. */
  readonly account?: string;
  readonly coins: readonly string[];
  /** Сколько ставить — как у «Снайпера». */
  readonly sizing: SniperSizing;
  readonly capUsd: number;
  /** Сколько держать сделку, минут; нет — 30 (бот запущен до выбора). */
  readonly holdMinutes?: number;
  /** Цель в долях стопа; нет — 1,5. */
  readonly reward?: number;
  /** Виртуальный бот: сколько взято со счёта и сколько у него сейчас без открытых сделок. */
  readonly deposit?: number;
  readonly cash?: number;
  readonly status: 'running' | 'paused' | 'stopped';
  readonly createdAt: number;
  readonly stoppedAt?: number;
  readonly positions: Readonly<Record<string, RicochetPosition>>;
  readonly closed: ReadonlyArray<RicochetPosition & { readonly closedAt: number; readonly realized: number | null; readonly reason: 'время' | 'площадка' | 'стоп' | 'цель' }>;
  readonly log: ReadonlyArray<{ readonly time: number; readonly symbol: string; readonly kind: 'enter' | 'skip' | 'exit'; readonly text: string }>;
}

export interface RicochetBotView {
  readonly bot: RicochetBot;
  readonly realized: number;
  readonly wins: number;
  readonly problem: { readonly text: string; readonly at: number } | null;
  readonly accountStopped: boolean;
  readonly bookReady: Readonly<Record<string, boolean>>;
  readonly prices: Readonly<Record<string, number | null>>;
  /** Виртуальный бот: деньги сейчас и сколько свободно; на счёте площадки — null. */
  readonly equity: number | null;
  readonly free: number | null;
}

export interface SniperBotView {
  readonly bot: SniperBot;
  readonly equity: number;
  /** Сколько бот может занять сейчас: меньшее из остатка предела залога и того, что есть на счёте площадки. */
  readonly free: number;
  /** Остаток предела залога бота. */
  readonly limitFree?: number;
  /** Сколько есть на счёте площадки под залог этого бота; null — виртуальный бот или счёт ещё не ответил. */
  readonly room?: number | null;
  readonly inUse: number;
  readonly prices: Readonly<Record<string, number | null>>;
  readonly ready: number;
  readonly missing: readonly string[];
  /** Открытые позиции бота на счёте площадки, как их видит она. */
  readonly onVenue?: Readonly<Record<string, { readonly entry: number; readonly left: number; readonly fixed: number }>>;
}


/** Что Core рассказывает о торговле. Ключи сюда не попадают. */
export interface TradingStatus {
  readonly mode: TradingMode;
  /** Пусто означает: боты только сообщают, сделки не исполняются. */
  readonly available: boolean;
  readonly venues: readonly {
    readonly venue: string;
    readonly mode: TradingMode;
    readonly connected: boolean;
    readonly reason?: string;
  }[];
}

export interface TradingBalance {
  readonly asset: string;
  readonly total: number;
  readonly free: number;
  readonly venue: string;
  readonly mode: TradingMode;
}

/** Ответ помощника: предложение плюс посчитанное движком. */
export interface AssistantAnswer {
  readonly spec: SignalSpec | null;
  readonly rationale: string;
  readonly changes: readonly SpecChange[];
  readonly report: SpecReport | null;
  readonly before: SpecReport | null;
  readonly problems: readonly string[];
}

export interface AssistantReview {
  readonly report: SpecReport;
  readonly suggestions: readonly Suggestion[];
  readonly funnel: readonly SpecFunnel[];
}

/** Состояние помощника: включён ли и отвечает ли модель. */
export interface AssistantStatus {
  readonly available: boolean;
  readonly provider: string;
  readonly ok: boolean;
  readonly reason?: string;
}

export interface SignalsFilter {
  readonly venue?: string;
  readonly symbol?: string;
  readonly tf?: Timeframe;
  readonly botId?: string;
  readonly side?: Side;
  readonly minScore?: number;
  readonly from?: number;
  readonly to?: number;
  readonly limit?: number;
}

export interface AuthUser {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  readonly telegram: boolean;
  /** Аватарка `data:`-строкой. Пусто — фотографии в Telegram нет. */
  readonly avatar: string | null;
}

/** Чем кончился запрос на вход через Telegram. */
export type TelegramLoginState = 'pending' | 'approved' | 'declined' | 'expired' | 'closed';

export const coreApi = {
  /* ------------------------------------------------------------ вход */

  /** Что доступно: подключён ли вход через Telegram. */
  authState: (): Promise<{ telegram: boolean; google: boolean }> => get('/api/auth/state'),

  /**
   * Кто вошёл, по свежим данным Core.
   *
   * Сведения о человеке лежат в браузере, чтобы имя было видно сразу
   * после перезагрузки. Но запись могла быть сделана раньше, чем
   * появились новые поля, — аватарки, например, в старых записях нет.
   */
  me: (): Promise<{ user: AuthUser }> => get('/api/auth/me'),

  register: (body: {
    email: string;
    password: string;
    name?: string;
  }): Promise<{ user: AuthUser; token: string }> => send('POST', '/api/auth/register', body),

  login: (body: { email: string; password: string }): Promise<{ user: AuthUser; token: string }> =>
    send('POST', '/api/auth/login', body),

  /** Начать вход через Telegram: ссылка на бота с одноразовым запросом. */
  telegramStart: (): Promise<{ token: string; link: string }> =>
    send('POST', '/api/auth/telegram/start', {}),

  /** Ответил ли человек боту. Спрашивается, пока он этого не сделал. */
  telegramResult: (
    token: string,
  ): Promise<{ state: TelegramLoginState; user?: AuthUser; token?: string }> =>
    get('/api/auth/telegram/result', { token }),

  logout: (): Promise<{ ok: boolean }> => send('POST', '/api/auth/logout', {}),

  venues: (): Promise<{ venues: VenueInfo[] }> => get('/api/venues'),

  symbols: (venue: string): Promise<{ symbols: SymbolInfo[] }> =>
    get('/api/symbols', { venue }),

  /**
   * Суточные сводки: цена, изменение, оборот.
   *
   * Отдельно от symbols намеренно: реестр инструментов меняется редко,
   * а сводки — постоянно, и запрашиваются они только когда открыт
   * список инструментов.
   */
  tickers: (venue: string): Promise<{ tickers: Ticker[] }> =>
    get('/api/tickers', { venue }),

  /** Радар рынка: строки всех монет, свежие события, трофеи и статистика «что было дальше». */
  radar: (events = 120): Promise<{ snapshot: RadarSnapshot; events: RadarEvent[]; trophies: RadarEvent[]; stats: RadarTypeStats[] }> =>
    get('/api/radar', { events: String(events) }),

  /* ---------------------------------------------------------- Мир */

  /** Лента мира и то, на что подписан спрашивающий. */
  worldFeed: (): Promise<{ events: Array<WorldEvent & { author: WorldAuthor }>; following: string[] }> => get('/api/world/feed'),

  worldSystems: (sort: 'fresh' | 'popular' | 'reliable' = 'fresh'): Promise<{ systems: WorldSystem[] }> =>
    get('/api/world/systems', { sort }),

  worldSystem: (id: string): Promise<{ system: WorldSystemPage }> => get(`/api/world/system/${encodeURIComponent(id)}`),

  worldTrader: (id: string): Promise<{ trader: WorldTrader }> => get(`/api/world/trader/${encodeURIComponent(id)}`),

  worldRatings: (): Promise<WorldRatings> => get('/api/world/ratings'),

  /** Опубликовать бота из своего профиля; повторно — выпустить новую версию. */
  worldPublish: (botId: string, note?: string, access?: 'open' | 'closed'): Promise<{ system: WorldSystem }> =>
    send('POST', '/api/world/publish', { botId, ...(note ? { note } : {}), ...(access ? { access } : {}) }),

  worldUnpublish: (id: string): Promise<{ ok: true }> => send('POST', '/api/world/unpublish', { id }),

  worldFollow: (kind: 'system' | 'trader' | 'indicator', id: string, on: boolean): Promise<{ ok: true }> =>
    send('POST', '/api/world/follow', { kind, id, on }),

  /** Форк или копия: сервер отдаёт бота целиком, терминал кладёт его в профиль. */
  /** Форк — бот целиком; копия — зеркало, которое повторяет сделки автора суммой `size`. */
  worldTake: (id: string, mode: 'fork' | 'copy', botId: string, size?: number): Promise<{ bot: BotDefinition }> =>
    send('POST', '/api/world/take', { id, mode, botId, ...(size !== undefined ? { size } : {}) }),

  /** Автор остановил систему — закрыть позиции копии сейчас. */
  worldCopyClose: (botId: string): Promise<{ closed: number }> => send('POST', '/api/world/copy-close', { botId }),

  worldUncopy: (botId: string): Promise<{ ok: true }> => send('POST', '/api/world/uncopy', { botId }),

  worldBio: (bio: string): Promise<{ ok: true }> => send('POST', '/api/world/bio', { bio }),

  worldIndicators: (sort: 'fresh' | 'popular' | 'reliable' = 'fresh'): Promise<{ indicators: WorldIndicator[] }> => get('/api/world/indicators', { sort }),

  worldIndicator: (id: string): Promise<{ indicator: WorldIndicatorPage }> => get(`/api/world/indicator/${encodeURIComponent(id)}`),

  /** Опубликовать свой индикатор; повторно — выпустить новую версию. */
  worldPublishIndicator: (specId: string, note?: string): Promise<{ indicator: WorldIndicator }> =>
    send('POST', '/api/world/publish-indicator', { specId, ...(note ? { note } : {}) }),

  worldUnpublishIndicator: (id: string): Promise<{ ok: true }> => send('POST', '/api/world/unpublish-indicator', { id }),

  /** «Взять себе»: сервер отдаёт описание под новым id, терминал кладёт его в профиль. */
  worldTakeIndicator: (id: string, specId: string): Promise<{ spec: SignalSpec }> => send('POST', '/api/world/take-indicator', { id, specId }),

  /** Сезон: правила, фаза, таблица по живой торговле и свои заявки. */
  season: (): Promise<SeasonView> => get('/api/season'),

  seasonEnter: (systemId: string): Promise<{ ok: true }> => send('POST', '/api/season/enter', { systemId }),

  seasonLeave: (systemId: string): Promise<{ ok: true }> => send('POST', '/api/season/leave', { systemId }),

  seasonEnterIndicator: (specId: string): Promise<{ ok: true }> => send('POST', '/api/season/enter-indicator', { specId }),

  seasonLeaveIndicator: (specId: string): Promise<{ ok: true }> => send('POST', '/api/season/leave-indicator', { specId }),

  /**
   * На каких таймфреймах у инструмента реально есть ряд.
   *
   * `bars: null` — источник отдаёт историю по запросу; спрашивать
   * дальше нечего. Число — сколько баров есть сейчас, и у накопительных
   * источников оно растёт само.
   */
  timeframes: (
    venue: string,
    symbol: string,
  ): Promise<{
    venue: string;
    symbol: string;
    timeframes: ReadonlyArray<{ timeframe: Timeframe; bars: number | null }>;
  }> => get('/api/timeframes', { venue, symbol }),

  /** Шапка одного инструмента: маркировка, индекс, финансирование, сутки. */
  instrumentStats: (
    venue: string,
    symbol: string,
  ): Promise<{ stats: InstrumentStats | null }> =>
    get('/api/instrument-stats', { venue, symbol }),

  /**
   * Структура рынка по нескольким биржам.
   *
   * Отдельным запросом от шапки инструмента: та обновляется каждые
   * несколько секунд и обязана быть быстрой, а здесь Core опрашивает
   * восемь площадок. Блок не имеет права задерживать график.
   */
  marketIntelligence: (symbol: string): Promise<{ intelligence: MarketIntelligence }> =>
    get('/api/market-intelligence', { symbol }),

  /**
   * Утренняя сводка рынка.
   *
   * Без параметров: она одна на всех и говорит о рынке, а не об
   * инструменте. Core держит её несколько минут и отдаёт всем
   * одинаковой — поэтому запрос дешёвый, а два человека читают одно и
   * то же объяснение одного и того же рынка.
   */
  marketBrief: (): Promise<{ brief: MarketBrief }> => get('/api/market-brief'),

  /**
   * Новости, объяснённые человеческим языком.
   *
   * Отдельным запросом от сводки: у них разный срок годности, и
   * подборка не должна задерживать текст о рынке.
   */
  news: (): Promise<{ news: NewsDigest }> => get('/api/news'),

  candles: (
    venue: string,
    symbol: string,
    tf: Timeframe,
    options: { from?: number; to?: number; limit?: number } = {},
  ): Promise<CandlesResponse> =>
    get('/api/candles', {
      venue,
      symbol,
      tf,
      ...(options.from !== undefined ? { from: options.from } : {}),
      ...(options.to !== undefined ? { to: options.to } : {}),
      ...(options.limit !== undefined ? { limit: options.limit } : {}),
    }),

  /**
   * Производный ряд по инструменту: открытый интерес, ставка, премия.
   *
   * Один метод на три адреса: запрос у них одинаковый, и три почти
   * одинаковые функции разошлись бы при первой же правке.
   */
  derivative: (
    kind: keyof typeof DERIVATIVE_PATHS,
    venue: string,
    symbol: string,
    tf: Timeframe,
    limit: number,
  ): Promise<DerivativeResponse> =>
    get(DERIVATIVE_PATHS[kind], { venue, symbol, tf, limit }),

  /**
   * Поток ордеров: бары по уровням цены за полуинтервал `[from, to)`.
   *
   * `mult` — во сколько раз свести базовые уровни; фактическое сведение
   * приходит в ответе.
   */
  orderFlow: (
    symbol: string,
    tf: Timeframe,
    mult: number,
    from: number,
    to: number,
  ): Promise<{ flow: OrderFlowSnapshot }> =>
    get('/api/order-flow', { symbol, tf, mult, from: Math.floor(from), to: Math.ceil(to) }),

  /** Что из потока ордеров собирает сервер: этап 3 включается отдельно. */
  orderFlowFeatures: (): Promise<{ features: OrderFlowFeatures }> => get('/api/order-flow/features'),

  /** Стакан: средние заявки по барам за полуинтервал `[from, to)`. */
  orderBook: (symbol: string, tf: Timeframe, from: number, to: number): Promise<{ book: OrderBookSnapshot }> =>
    get('/api/order-book', { symbol, tf, from: Math.floor(from), to: Math.ceil(to) }),

  signals: (filter: SignalsFilter = {}): Promise<{ signals: Signal[] }> => {
    const params: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined) params[key] = value as string | number;
    }
    return get('/api/signals', params);
  },

  signal: (id: string): Promise<Signal> => get(`/api/signals/${encodeURIComponent(id)}`),

  bots: (): Promise<{ bots: BotInfo[] }> => get('/api/bots'),

  /** Параметры применяются на лету, без перезапуска бота. */
  setBotParams: (
    botId: string,
    params: Record<string, unknown>,
  ): Promise<BotInfo> =>
    send('PUT', `/api/bots/${encodeURIComponent(botId)}/params`, params),

  analytics: (filter: {
    venue?: string;
    symbol?: string;
    botId?: string;
    horizonBars?: number;
    targetPercent?: number;
    stopPercent?: number;
  } = {}): Promise<{ stats: BotStats[] }> => {
    const params: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined) params[key] = value as string | number;
    }
    return get('/api/analytics/stats', params);
  },

  /** Смена потоков перезапускает подписки и прогрев бота. */
  setBotInstruments: (
    botId: string,
    instruments: readonly BotInstrumentRef[],
  ): Promise<BotInfo> =>
    send('PUT', `/api/bots/${encodeURIComponent(botId)}/instruments`, { instruments }),

  /**
   * Состояние торговли.
   *
   * Ключей здесь нет и быть не может: Core отдаёт режим исполнения и
   * ответ на единственный вопрос, который терминалу нужен, — площадка
   * на связи или нет.
   */
  trading: (): Promise<TradingStatus> => get('/api/trading'),

  /* ---- боты-стратегии: сетка и DCA на виртуальном счёте ---- */

  strategies: (): Promise<StrategiesResponse> => get('/api/strategies'),

  createStrategy: (input: { name?: string; venue: string; symbol: string; config: StrategyConfig; account?: string }): Promise<{ bot: StrategyView }> =>
    send('POST', '/api/strategies', input),

  strategyAction: (id: string, action: 'pause' | 'resume' | 'stop'): Promise<{ bot: StrategyView }> =>
    send('POST', `/api/strategies/${encodeURIComponent(id)}/${action}`, {}),

  removeStrategy: (id: string): Promise<{ ok: boolean }> => send('DELETE', `/api/strategies/${encodeURIComponent(id)}`, {}),

  resetStrategies: (): Promise<{ account: StrategyAccount }> => send('POST', '/api/strategies-reset', {}),

  ebb: (): Promise<{ bots: EbbBotView[] }> => get('/api/ebb'),

  createEbb: (input: { name?: string; account?: string; sizing: SniperSizing; maxPositions: number; deposit?: number }): Promise<{ bot: EbbBot }> => send('POST', '/api/ebb', input),

  updateEbb: (id: string, input: { sizing?: SniperSizing; maxPositions?: number }): Promise<{ bot: EbbBot }> => send('PUT', `/api/ebb/${encodeURIComponent(id)}`, input),

  ebbAction: (id: string, action: 'pause' | 'resume' | 'stop'): Promise<{ bot: EbbBot }> => send('POST', `/api/ebb/${encodeURIComponent(id)}/${action}`, {}),

  removeEbb: (id: string): Promise<{ ok: boolean }> => send('DELETE', `/api/ebb/${encodeURIComponent(id)}`, {}),

  spring: (): Promise<{ bots: SpringBotView[] }> => get('/api/spring'),

  createSpring: (input: { name?: string; account?: string; sizing: SniperSizing; maxPositions: number; deposit?: number }): Promise<{ bot: SpringBot }> => send('POST', '/api/spring', input),

  updateSpring: (id: string, input: { sizing?: SniperSizing; maxPositions?: number }): Promise<{ bot: SpringBot }> => send('PUT', `/api/spring/${encodeURIComponent(id)}`, input),

  springAction: (id: string, action: 'pause' | 'resume' | 'stop'): Promise<{ bot: SpringBot }> => send('POST', `/api/spring/${encodeURIComponent(id)}/${action}`, {}),

  removeSpring: (id: string): Promise<{ ok: boolean }> => send('DELETE', `/api/spring/${encodeURIComponent(id)}`, {}),

  guard: (): Promise<{ accounts: GuardView[] }> => get('/api/guard'),

  updateGuard: (accountId: string, input: { enabled?: boolean; targetPercent?: number; reservePercent?: number }): Promise<{ account: GuardView }> => send('PUT', `/api/guard/${encodeURIComponent(accountId)}`, input),

  releaseGuard: (accountId: string): Promise<{ account: GuardView }> => send('POST', `/api/guard/${encodeURIComponent(accountId)}/release`, {}),

  /** Раздел упал у человека — прислать, что сломалось. */
  clientError: (input: { section: string; kind: 'chunk' | 'render'; message: string; stack: string; bundle: string; agent: string }): Promise<{ ok: boolean }> => send('POST', '/api/client-error', input),

  seesaw: (): Promise<{ bots: SeesawBotView[] }> => get('/api/seesaw'),

  createSeesaw: (input: { name?: string; account?: string; capital?: number; deposit?: number; leverage: number; stop: number | null }): Promise<{ bot: SeesawBot }> => send('POST', '/api/seesaw', input),

  /** Аварийный стоп «Качелей» в процентах; `null` — снять. */
  seesawStop: (id: string, stop: number | null): Promise<{ bot: SeesawBot }> => send('PUT', `/api/seesaw/${encodeURIComponent(id)}/stop`, { stop }),

  seesawAction: (id: string, action: 'pause' | 'resume' | 'stop'): Promise<{ bot: SeesawBot }> => send('POST', `/api/seesaw/${encodeURIComponent(id)}/${action}`, {}),

  removeSeesaw: (id: string): Promise<{ ok: boolean }> => send('DELETE', `/api/seesaw/${encodeURIComponent(id)}`, {}),

  riskCap: (): Promise<{ accounts: RiskCapView[] }> => get('/api/risk-cap'),

  updateRiskCap: (accountId: string, input: { enabled?: boolean; percent?: number }): Promise<{ account: RiskCapView }> => send('PUT', `/api/risk-cap/${encodeURIComponent(accountId)}`, input),

  ricochet: (): Promise<{ bots: RicochetBotView[] }> => get('/api/ricochet'),
  /** «Фибо-пульт»: сделки по сеткам человека, свежие первыми. */
  fibo: (): Promise<{ runs: FiboView[] }> => get('/api/fibo'),
  closeFibo: (drawingId: string): Promise<{ run: FiboView }> => send('POST', `/api/fibo/${encodeURIComponent(drawingId)}/close`, {}),

  createRicochet: (input: { name?: string; account?: string; coins: readonly string[]; sizing: SniperSizing; capUsd: number; deposit?: number; holdMinutes?: number; reward?: number }): Promise<{ bot: RicochetBot }> => send('POST', '/api/ricochet', input),

  /** Боты, оповещения о сделках которых человек выключил. */
  botNotify: (): Promise<{ muted: string[] }> => get('/api/bot-notify'),
  setBotNotify: (botId: string, on: boolean): Promise<{ muted: string[] }> => send('PUT', `/api/bot-notify/${encodeURIComponent(botId)}`, { on }),
  updateRicochet: (id: string, input: { sizing?: SniperSizing; capUsd?: number; coins?: string[]; holdMinutes?: number; reward?: number }): Promise<{ bot: RicochetBot }> => send('PUT', `/api/ricochet/${encodeURIComponent(id)}`, input),

  ricochetAction: (id: string, action: 'pause' | 'resume' | 'stop'): Promise<{ bot: RicochetBot }> => send('POST', `/api/ricochet/${encodeURIComponent(id)}/${action}`, {}),

  removeRicochet: (id: string): Promise<{ ok: boolean }> => send('DELETE', `/api/ricochet/${encodeURIComponent(id)}`, {}),

  sniper: (): Promise<{ bots: SniperBotView[] }> => get('/api/sniper'),

  createSniper: (input: { name?: string; venue: SniperVenue; markets: readonly SniperMarketRef[]; sizing: SniperSizing; deposit: number; account?: string }): Promise<{ bot: SniperBotView }> =>
    send('POST', '/api/sniper', input),

  updateSniper: (id: string, input: { name?: string; markets?: readonly SniperMarketRef[]; sizing?: SniperSizing; deposit?: number }): Promise<{ bot: SniperBotView }> =>
    send('PUT', `/api/sniper/${encodeURIComponent(id)}`, input),

  sniperAction: (id: string, action: 'pause' | 'resume' | 'stop'): Promise<{ bot: SniperBotView }> =>
    send('POST', `/api/sniper/${encodeURIComponent(id)}/${action}`, {}),

  removeSniper: (id: string): Promise<{ ok: boolean }> => send('DELETE', `/api/sniper/${encodeURIComponent(id)}`, {}),

  positions: (): Promise<{ positions: Position[] }> => get('/api/trading/positions'),

  tradingBalances: (): Promise<{ balances: TradingBalance[] }> => get('/api/trading/balances'),

  trades: (filter: { botId?: string; limit?: number } = {}): Promise<{ trades: Trade[] }> => {
    const params: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined) params[key] = value as string | number;
    }
    return get('/api/trading/trades', params);
  },

  /** Биржевые счета человека и адрес сервера для привязки ключа. */
  accounts: (): Promise<{ accounts: ExchangeAccount[]; serverIp: string | null }> => get('/api/accounts'),

  /** Подключить счёт: ключи уходят на сервер один раз и обратно не возвращаются. */
  connectAccount: (input: { venue: ExchangeAccountVenue; mode: 'live' | 'demo'; key: string; secret: string; label: string; sub?: string }): Promise<{ account: ExchangeAccount }> =>
    send('POST', '/api/accounts', input),

  /** Счета ключа Upscale — выбрать, какой подключить. Ключ при этом не запоминается. */
  discoverAccounts: (key: string): Promise<{ accounts: UpscaleAccountChoice[] }> => send('POST', '/api/accounts/discover', { venue: 'upscale', key }),

  /** Ручные позиции виртуального счёта — со стопом и целью. */
  manualPositions: (): Promise<{ positions: ManualPosition[] }> => get('/api/manual/positions'),
  /** Пульт сделки: открыть позицию по рынку. `account` пуст — виртуальный счёт. */
  manualOpen: (input: {
    account?: string;
    venue: string;
    symbol: string;
    side: 'LONG' | 'SHORT';
    quantity: number;
    leverage: number;
    stop?: number | null;
    take?: number | null;
    /** «По цене» или «стоп-вход»; пусто — по рынку. */
    kind?: 'limit' | 'stop';
    /** Цена отложенного входа. */
    level?: number;
  }): Promise<{ price: number; quantity: number; pending?: string }> => send('POST', '/api/manual/open', input),
  /** Отложенные входы выбранного счёта; `account` пуст — виртуальный. */
  manualPending: (account: string | null): Promise<{ pending: ManualPending[] }> => get('/api/manual/pending', account ? { account } : {}),
  /** Снять отложенный вход. */
  manualCancel: (input: { account?: string; id: string }): Promise<{ ok: boolean }> => send('POST', '/api/manual/cancel', input),
  /** Поставить, передвинуть или снять стоп и цель позиции. */
  manualProtect: (input: { account?: string; venue?: string; symbol?: string; native?: string; side: 'LONG' | 'SHORT'; stop: number | null; take: number | null }): Promise<{ ok: boolean }> =>
    send('POST', '/api/manual/protect', input),
  /** Закрыть позицию по рынку: целиком или долю. */
  manualClose: (input: { account?: string; venue?: string; symbol?: string; native?: string; side: 'LONG' | 'SHORT'; fraction: number }): Promise<{ price: number; quantity: number; realized: number }> =>
    send('POST', '/api/manual/close', input),

  /** Закрыть по рынку все позиции бота на виртуальном счёте — перед его удалением. */
  manualCloseBot: (botId: string): Promise<{ closed: number; realized: number }> => send('POST', '/api/manual/close-bot', { botId }),

  /** Табло счёта: числа с площадки, кривая средств, позиции с защитой. */
  accountBoard: (id: string): Promise<AccountBoard> => get(`/api/accounts/${encodeURIComponent(id)}/board`),

  accountOverview: (id: string): Promise<ExchangeAccountOverview> => get(`/api/accounts/${encodeURIComponent(id)}/overview`),

  checkAccount: (id: string): Promise<{ account: ExchangeAccount }> => send('POST', `/api/accounts/${encodeURIComponent(id)}/check`, {}),

  /** Боты человека на биржевых счетах: по идентификатору бота. */
  liveBots: (): Promise<{ bots: Record<string, LiveBotStatus>; stopped: readonly string[] }> => get('/api/live-bots'),

  /** «Остановить всё» на счёте: закрыть позиции ботов и запретить им новые входы. */
  stopAccount: (id: string): Promise<{ closed: number; failed: readonly string[] }> => send('POST', `/api/accounts/${encodeURIComponent(id)}/stop`, {}),
  /** Снова разрешить ботам входить на этом счёте. */
  resumeAccount: (id: string): Promise<{ ok: boolean }> => send('POST', `/api/accounts/${encodeURIComponent(id)}/resume`, {}),

  /** Проверка заявок: наименьшая сделка на демо-счёте — открыть, защитить, закрыть. */
  selfTestAccount: (id: string): Promise<AccountSelfTest> => send('POST', `/api/accounts/${encodeURIComponent(id)}/self-test`, {}),

  removeAccount: (id: string): Promise<{ ok: boolean }> => send('DELETE', `/api/accounts/${encodeURIComponent(id)}`, {}),

  /** Итог каждого бота за всё время — не по последним 500 сделкам счёта. */
  botTotals: (): Promise<{ totals: Record<string, BotTotal> }> => get('/api/trading/bot-totals'),

  /** Доступен ли помощник. Ключ сюда не приходит ни в каком виде. */
  assistant: (): Promise<AssistantStatus> => get('/api/assistant'),

  /**
   * Спросить помощника.
   *
   * Возвращает предложение и то, что из него выйдет на истории. Ничего
   * не применяет: применяет человек нажатием, и до него в профиле не
   * меняется ничего.
   */
  assistantAsk: (body: {
    prompt: string;
    venue: string;
    symbol: string;
    timeframe: Timeframe;
    spec: unknown;
  }): Promise<AssistantAnswer> => send('POST', '/api/assistant/ask', body),

  /** Разбор истории и готовые правки. Модель для этого не нужна. */
  assistantReview: (body: { venue: string; spec: unknown }): Promise<AssistantReview> =>
    send('POST', '/api/assistant/review', body),

  settings: (): Promise<UserSettings> => get('/api/settings'),

  /**
   * Настроение рынка: индекс страха и жадности.
   *
   * `null` в ответе — источник молчит и прошлого значения у Core нет.
   * Это не ошибка: главная просто не покажет карточку.
   */
  mood: (): Promise<{ mood: MarketMood | null }> => get('/api/mood'),

  /**
   * Вход из мини-приложения Telegram.
   *
   * Строку `initData` даёт сам Telegram, и подписана она токеном
   * нашего бота: проверить её может только Core, подделать — никто.
   */
  telegramWebAppLogin: (
    initData: string,
  ): Promise<{
    token: string;
    user: { id: string; name: string; email: string | null; telegram: boolean; avatar?: string | null };
  }> => send('POST', '/api/auth/telegram/web-app', { initData }),

  saveSettings: (settings: UserSettings): Promise<UserSettings> =>
    send('PUT', '/api/settings', settings),

  /**
   * Сохранить профиль, сказав, от какой его версии отталкивались.
   *
   * Отдельно от `saveSettings`, потому что ответ здесь двоякий:
   * записали — или не записали, потому что профиль успел измениться, и
   * тогда возвращается его нынешнее содержимое. Втиснуть это в
   * «вернёт настройки или бросит» значило бы потерять как раз то, ради
   * чего всё и затевалось: свежую версию, которой вкладка перечитается.
   */
  saveSettingsFrom: async (
    base: number | null,
    settings: UserSettings,
  ): Promise<
    { readonly ok: true; readonly saved: UserSettings } | { readonly ok: false; readonly current: UserSettings }
  > => {
    const url = new URL(`${BASE}/api/settings`, globalThis.location.origin);
    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: headers({
        'content-type': 'application/json',
        ...(base === null ? {} : { 'x-settings-base': String(base) }),
      }),
      body: JSON.stringify(settings),
    });

    if (response.status === 409) {
      const payload = (await response.json().catch(() => null)) as {
        current?: UserSettings;
      } | null;
      // Тела нет — значит перечитать нечем, и молчать нельзя: пусть
      // обычный путь ошибки уведёт вкладку в «нет связи».
      if (!payload?.current) throw new CoreApiError(409, 'Профиль изменился, но не пришёл');
      return { ok: false, current: payload.current };
    }

    if (!response.ok) {
      checkAuth(response.status, '/api/settings');
      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new CoreApiError(response.status, payload?.error ?? `Core ответил ${response.status}`);
    }

    return { ok: true, saved: (await response.json()) as UserSettings };
  },

  /**
   * Состояние канала Telegram.
   *
   * Отвечает и тогда, когда канал недоступен: терминалу нужно знать
   * причину, чтобы объяснить её человеку, а не показывать галочку,
   * которая ничего не включает.
   */
  /** Остановить бота: ему перестают идти свечи, процесс остаётся жив. */
  stopBot: (id: string): Promise<{ status: string }> =>
    send('POST', `/api/bots/${encodeURIComponent(id)}/stop`, {}),

  /** Запустить остановленного: потоки возвращаются те же, что были. */
  startBot: (id: string): Promise<{ status: string }> =>
    send('POST', `/api/bots/${encodeURIComponent(id)}/start`, {}),

  /** Состояние платформы: живо ли Core и что оно ведёт. */
  health: (): Promise<HealthState> => get('/api/health'),

  telegram: (): Promise<TelegramState> => get('/api/telegram'),

  /** Код привязки: человек отправляет его боту в Telegram. */
  telegramCode: (): Promise<{ code: string }> => send('POST', '/api/telegram/code', {}),

  telegramUnlink: (): Promise<{ ok: boolean }> => send('DELETE', '/api/telegram/links', {}),
};

export interface HealthState {
  readonly status: string;
  readonly venues: readonly string[];
  readonly subscribers: number;
  readonly signals: { readonly accepted: number; readonly rejected: number } | null;
  readonly bots: readonly { readonly id: string; readonly status: string }[] | null;
  readonly push: { readonly sent: number; readonly skipped: number } | null;
}

export interface TelegramState {
  /** Задан ли токен бота на сервере. */
  readonly available: boolean;
  readonly linked: boolean;
  readonly accounts?: readonly string[];
  /** Почему канал недоступен — словами, а не кодом. */
  readonly reason?: string;
}
